import { hasAnyAccessToResource } from "api/general";

import { useReward } from "api/rewards";
import { useAppDispatch } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderActionButton } from "components/Header/Subheader/SubheaderActionButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { createHeadItems, OLTable, Row } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import React, { useState } from "react";
import { clearTablePage } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { REWARD_CATEGORIES_LIST_ID } from "state/tables/types";
import { ListingActions, ListResponse } from "types/listing";
import { RewardCategoriesModals } from "./RewardCategoriesModals";

export enum ModalMode {
  Activate,
  Deactivate,
  Add,
  Edit,
}

export function ListOfRewardCategories() {
  const { message } = useMessage();
  const { listing, updateListing } = useListing(REWARD_CATEGORIES_LIST_ID);
  const appDispatch = useAppDispatch();
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const { setColumnOrdering } = useTablesStateHelpers();
  const { useRewardCategoriesList } = useReward();
  const { data: rewardCategories } = useRewardCategoriesList({
    order: listing.order || "asc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "name",
    dynamicSearch,
  }) as { data: ListResponse };

  const headItems = createHeadItems([
    {
      headerText: message({ id: "name" }),
      property: "name",
      isSortable: true,
    },
  ]);

  function createItems(items: any[]): Row[] {
    return items.map((item) => ({
      cells: [{ content: item.name, property: "name" }],
      id: item.rewardCategoryId,
      metadata: { disabled: !item.active },
    }));
  }

  const items = rewardCategories?.items
    ? createItems(rewardCategories.items)
    : [];

  const [selectedModal, setSelectedModal] = useState<{
    mode: number | null;
    category?: string;
    isOpen: boolean;
  }>({ mode: null, category: "", isOpen: false });

  const hasAnyLanguagePermission = hasAnyAccessToResource("LANGUAGE");

  return (
    <>
      <TopHeader title={message({ id: "rewardsCategories" })} />
      <Subheader>
        <SubheaderWrapper alignRight>
          <SubheaderActionButton
            variant="contained"
            text={message({ id: "addCategory" })}
            onClick={() => {
              setSelectedModal({ mode: ModalMode.Add, isOpen: true });
            }}
            disabled={!hasAnyLanguagePermission}
            data-test-id="reward-categories-add-category-button"
          />
        </SubheaderWrapper>
      </Subheader>
      <Subheader>
        <SubheaderWrapper alignRight>
          <DynamicSearch
            property="name"
            setDynamicSearch={setDynamicSearch}
            onSearch={() =>
              appDispatch(
                clearTablePage({ tableName: REWARD_CATEGORIES_LIST_ID }),
              )
            }
          />
        </SubheaderWrapper>
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={REWARD_CATEGORIES_LIST_ID}
        data-test-id="reward-categories"
        sort={{
          order: listing.order || "asc",
          property: listing.property || "name",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        actions={{
          body: [
            {
              label: message({ id: "activate" }),
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setSelectedModal({
                  mode: ModalMode.Activate,
                  category: id as string,
                  isOpen: true,
                });
              },
              hideCondition: (item: Row) => !item.metadata?.disabled,
            },
            {
              label: message({ id: "deactivate" }),
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setSelectedModal({
                  mode: ModalMode.Deactivate,
                  category: id as string,
                  isOpen: true,
                });
              },
              hideCondition: (item: Row) => item.metadata?.disabled,
            },
            {
              label: message({ id: "edit" }),
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setSelectedModal({
                  mode: ModalMode.Edit,
                  category: id as string,
                  isOpen: true,
                });
              },
              disableCondition: () => !hasAnyLanguagePermission,
            },
          ],
        }}
        columns={rewardCategories && headItems}
        rows={rewardCategories && items}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(
            rewardCategories?.total.filtered,
            rewardCategories?.total.all,
          ),
          onChangePage: (page) =>
            updateListing({ payload: page, type: ListingActions.CHANGE_PAGE }),
          onChangeRowsPerPage: (rows) =>
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            }),
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
      />
      <RewardCategoriesModals
        selectedModal={selectedModal}
        close={() => {
          setSelectedModal({ mode: null, category: "", isOpen: false });
        }}
      />
    </>
  );
}
